
import { Component, ElementRef, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import html2canvas from 'html2canvas';
import { AbstractComponent, Attendance, AttendanceService, AttendanceStatusEnum, Channel, ChannelService, ChartGroupItem, ChartSeriesItem, ConfirmationComponent, DateUtil, Department, DepartmentService, ExcelReportSummaryData, ExportService, Pager, SalesMonitor, SalesMonitorService, Team, TeamService, User, UserService } from 'lib-trend-core';
import { concatMap, debounceTime, distinctUntilChanged, Observable, Subject, switchMap, takeUntil } from 'rxjs';

interface SalesMonitorStatusItem {
  _id: {
    day: string;
  };
  name: string;
  total: number;
  attendancesWithStatus: number;
  closedSale: boolean | null;
  lostSale: boolean | null;
}

interface SalesMonitorKpis {
  totalSales: number;
  closedSales: number;
  conversionRate: string;
  conversionRateByValue: string;
  totalGenerated: string;
  totalEarned: string;
  totalLost: string;
  salesLost: number;
}

@Component({
    selector: 'app-report-sales-monitor',
    templateUrl: './report-sales-monitor.v2.component.html',
    // templateUrl: './report-sales-monitor.component.html',
    styleUrl: './report-sales-monitor.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportSalesMonitorComponent extends AbstractComponent implements OnInit {
  private termOfSearch: Subject<string> = new Subject<string>();
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  @ViewChild('chartContainer') chartContainer!: ElementRef;

  pager: Pager<Attendance> = new Pager<Attendance>({ perPage: 10 });

  isPicker: boolean = false;
  listDepartment: Array<Department> = new Array<Department>();
  listChannel: Array<Channel> = new Array<Channel>();
  listUser: Array<User> = new Array<User>();
  listTeam: Array<Team> = new Array<Team>();
  listSalesMonitor: Array<SalesMonitor> = new Array<SalesMonitor>();
  listTeamChannel: Array<Team> = new Array<Team>();
  listDepartmentChannel: Array<Department> = new Array<Department>();
  listSalesMonitorChanel: Array<SalesMonitor> = new Array<SalesMonitor>();
  public attendantsByTeam: Array<any> = new Array<any>();

  attendanceStatusEnum = AttendanceStatusEnum;

  listObservable: Observable<Pager<Attendance>>;


  labelDepartment: string = 'report.salesMonitor.departmentsFilterLabel';
  labelChannel: string = 'report.salesMonitor.channelsFilterLabel';
  labelUser: string = 'report.salesMonitor.tableAttendant';
  labelPeriod: string = '';
  labelSalesMonitor: string = 'report.salesMonitor.monitorFilterLabel';

  leadSource: string = 'Todos';
  filteredSalesMonitors: Array<SalesMonitor> = [];

  isLoading: boolean = false;

  salesMonitorToBarChart: ChartGroupItem[] = [];
  salesBySourceChart: ChartSeriesItem[] = [];
  salesMonitorKpis: SalesMonitorKpis;

  colorScheme: any = {
    domain: [
      '#3B82F6',
      '#10B981',
      '#EF4444',
    ]
  };

  private dynamicColors: Map<string, string> = new Map();
  private colorIndex: number = 0;
  private dynamicColorPalette: string[] = [

    '#667085',

  ];
  customColors: Array<{ name: string; value: string }> = [];

  breadcrumb = [
    { href: '/report', label: 'report.breadcrumb.reports' },
    { label: 'report.breadcrumb.bcSalesMonitor' },
  ];

  constructor(
    injector: Injector,
    private userService: UserService,
    private departmentService: DepartmentService,
    private channelService: ChannelService,
    private teamService: TeamService,
    private attendanceService: AttendanceService,
    private saleMonitorServie: SalesMonitorService,
    public exportService: ExportService,
    public dialog: MatDialog
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.initializeData();
  }

  private initializeData(): void {
    this.getListChannel();
    this.getSalesMonitorList();
    this.getAttendanceSalesMonitor();

    if (this.isAdmin()) {
      this.getListUser();
      this.getListDepartment();
    } else if (this.isSupervisor() || this.isAttendant()) {
      this.getListTeam();
    }
  }


  private setupForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.formGroup = this.formBuilder.group({
      channel: [null],
      department: [null],
      user: [null],
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
      salesMonitor: [null],
    });

    this.labelPeriod = `${DateUtil.toFormart(thirtyDaysAgo, 'dd/MM/yyyy')} - ${DateUtil.toFormart(today, 'dd/MM/yyyy')}`;

    let previousValue = this.formGroup.value;
    this.formGroup.valueChanges.pipe(debounceTime(100)).subscribe((currentValue) => {
      if (
        previousValue.channel !== currentValue.channel ||
        previousValue.user !== currentValue.user ||
        previousValue.dateRange.dateStart !== currentValue.dateRange.dateStart ||
        previousValue.dateRange.dateEnd !== currentValue.dateRange.dateEnd ||
        previousValue.department !== currentValue.department
      ) {
        this.pager.page = 1;

      }
      this.getAttendanceSalesMonitor();
      previousValue = currentValue;
    })
  }

  private getListChannel(): void {
    this.channelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channels: Array<Channel>) => {
          this.listChannel = channels;
        }
      });
  }

  private getSalesMonitorList(): void {
    this.saleMonitorServie.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (salesMonitors: Array<SalesMonitor>) => {
          this.listSalesMonitor = salesMonitors;
        },
        error: (err) => this.alertService.error(err.error?.message ?? this.t('report.salesMonitor.unexpectedError')),
      });
  }

  private getListDepartment(): void {
    this.departmentService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (departments: Array<Department>) => {
          this.listDepartment = departments;
        },
      })
  }

  private getListUser(): void {
    this.userService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users: Array<User>) => {
          this.listUser = users;
        },
      })
  }

  private getListTeam(): void {
    this.teamService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (teamList: Array<Team>) => {
          this.listTeam = teamList
            .filter((team: Team) => {
              const userId = this.getCurrentUserUser()._id;
              const isAgent = team.agents.some(
                (agent: User) => agent._id === userId
              )
              const isSupervisor = team.supervisors.some(
                (supervisor: User) => supervisor._id === userId
              )
              return isAgent || isSupervisor;
            })
            .filter(
              (team: Team) => team.department && team.department.removed !== true
            )
        },
      })
  }

  private getAttendanceSalesMonitor(): void {
    this.loadingContent = true;

    const formValues = this.formGroup.getRawValue();

    const filters: Record<string, unknown> = {
      channel: formValues.channel,
      user: formValues.user,
      department: formValues.department,
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
      salesMonitor: formValues.salesMonitor,
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.attendanceService
      .getReportAttendancesSalesMonitor(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<Attendance>) => {
          this.pager = pager;
          this.setupObservableSearch();
        },
        complete: () => (this.loadingContent = false),
        error: (err) =>
          this.alertService.error(err.error?.message ?? this.t('report.salesMonitor.unexpectedError')),
      });
  }

  private setupObservableSearch(): void {
    this.listObservable = this.termOfSearch.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((term) => {
        this.searchString = term
        return this.attendanceService.getReportAttendancesSalesMonitor(this.pager.page, this.pager.perPage, term, this.searchParams);
      })
    )
    this.listObservable.subscribe({
      next: (pager: Pager<Attendance>) => {
        this.pager = pager;
      },
      complete: () => (this.loadingContent = false),
      error: (err) => this.alertService.error(err.error.message),
    })
    this.getSalesMonitorKpis()
  }

  private fetchAttendancesRecursively(attendances: Attendance[], pager: Pager<Attendance>): Observable<Array<Attendance>> {
    return this.attendanceService.getReportAttendancesSalesMonitor(pager.page, pager.perPage, this.searchString, this.searchParams)
      .pipe(
        takeUntil(this.destroy$),
        concatMap(newPager => {
          const allAttendances = [...attendances, ...newPager.list];
          if (allAttendances.length >= newPager.total) {
            return new Observable<Array<Attendance>>(observer => {
              observer.next(allAttendances);
            });
          } else {
            return this.fetchAttendancesRecursively(
              allAttendances,
              { ...pager, page: pager.page + 1 }
            );
          }
        })
      );
  }

  searchByChannel(channel: Channel): void {
    if (channel) {
      this.labelChannel = channel.name;
      this.formGroup.get('channel').setValue(channel._id);
      if (this.listDepartment) {
        this.listDepartmentChannel = this.listDepartment.filter((dept: Department) => dept.channel?.toString() === channel._id);
      }
      if (this.listTeam) {
        this.listTeamChannel = this.listTeam.filter((team: Team) => team.department.channel?.toString() === channel._id);
      }
      if (this.listSalesMonitor) {
        this.listSalesMonitorChanel = this.listSalesMonitor.filter((salesMonitor: SalesMonitor) => salesMonitor.channel?.toString() === channel._id);
      }
    } else {
      this.labelChannel = 'report.salesMonitor.channelsFilterLabel';
      this.formGroup.get('channel').setValue(null);
      this.listDepartmentChannel = [];
      this.listTeamChannel = [];
      this.listSalesMonitorChanel = [];
    }
    this.pager.page = 1;
  }

  searchByUser(user: User): void {
    if (user) {
      this.labelUser = user.name;
      this.formGroup.get('user').setValue(user._id);
    } else {
      this.labelUser = 'report.salesMonitor.tableAttendant';
      this.formGroup.get('user').setValue(null);
    }

  }

  searchBySalesMonitor(salesMonitor: SalesMonitor): void {
    if (salesMonitor) {
      this.labelSalesMonitor = salesMonitor.name;
      this.formGroup.get('salesMonitor').setValue(salesMonitor._id);
    } else {
      this.labelSalesMonitor = 'report.salesMonitor.monitorFilterLabel';
      this.formGroup.get('salesMonitor').setValue(null);
    }
    this.pager.page = 1;
    this.getAttendanceSalesMonitor();
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getAttendanceSalesMonitor();
  }

  onPeriodChange(event: MatSelectChange) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      if (period === 1) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(),
          dateEnd: new Date(),
        });
      } else if ([7, 15, 30, 60].includes(period)) {
        this.formGroup.get('dateRange')?.patchValue({
          dateStart: new Date(today.setDate(today.getDate() - period)),
          dateEnd: new Date(),
        });
      }
      this.picker?.close();
    }
  }

  onPickerClose() {
    const dateStart = this.formGroup.get('dateRange')?.get('dateStart')?.value;
    const dateEnd = this.formGroup.get('dateRange')?.get('dateEnd')?.value;

    if (dateStart && dateEnd) {
    } else {
    }
  }

  openPicker(): void {
    this.isPicker = true;
    setTimeout(() => {
      this.picker.open();
    }, 300);
  }

  search(term: string) {
    this.searchString = term;
    this.termOfSearch.next(term);
  }


  searchByDepartment(department: Department): void {
    if (department) {
      this.labelDepartment = department.name;
      this.formGroup.get('department').setValue(department._id);
      if (this.isAdmin()) {
        this.teamService.getList().pipe(takeUntil(this.destroy$)).subscribe((teams: Array<Team>) => {
          this.attendantsByTeam = teams
            .filter((team: Team) => team.department._id === department._id && team.agents?.length)
            .flatMap((team: Team) => team.agents);
        });
      } else if ((this.isSupervisor() || this.isAttendant())) {
        this.attendantsByTeam = this.listTeam
          .filter((team: Team) => team.department._id === department?._id && team.agents?.length)
          .flatMap((team: Team) => team.agents);
      }
    } else {
      this.labelDepartment = 'report.salesMonitor.departmentsFilterLabel';
      this.formGroup.get('department').setValue(null);
    }
  }

  getSalesMonitorKpis(): void {
    this.attendanceService.getSalesMonitorKPIs(this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (salesMonitors: any) => {
          this.salesMonitorKpis = {
            totalSales: salesMonitors?.kpis?.totalSales ?? 0,
            closedSales: salesMonitors?.kpis?.closedSales ?? 0,
            conversionRate: this.getSalesConversionRate(salesMonitors?.kpis?.conversionRate) ?? '0%',
            conversionRateByValue: this.getSalesConversionRate(salesMonitors?.kpis?.conversionRateByValue) ?? '0%',
            totalGenerated: this.getSalesAmountCurrency(salesMonitors?.kpis?.totalGenerated) ?? 'R$ 0,00',
            totalEarned: this.getSalesAmountCurrency(salesMonitors?.kpis?.totalEarned) ?? 'R$ 0,00',
            totalLost: this.getSalesAmountCurrency(salesMonitors?.kpis?.totalLost) ?? 'R$ 0,00',
            salesLost: salesMonitors?.kpis?.saleLost ?? 0,
          };

          this.getSalesBySourcePieChartData(salesMonitors?.salesBySource);
          this.getSalesMonitorBarChartData(salesMonitors?.salesByStatus);
        },
        error: (err) => this.alertService.error(err.error?.message ?? this.t('report.salesMonitor.unexpectedError')),
      });
  }

  private getSalesAmountCurrency(amount: number): string {
    const formatted = (amount !== undefined && amount !== null)
      ? amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : 'R$ 0,00';

    return formatted;
  }

  private getSalesConversionRate(rate: number): string {
    const formatted = (rate !== undefined && rate !== null)
      ? Number.isInteger(rate)
        ? `${rate}%`
        : `${rate.toFixed(2)}%`
      : '0%';

    return formatted;
  }

  getSalesMonitorBarChartData(salesToChart: SalesMonitorStatusItem[]): void {
    this.dynamicColors.clear();
    this.colorIndex = 0;

    if (!Array.isArray(salesToChart)) {
      this.salesMonitorToBarChart = [];
      this.customColors = [];
      return;
    }

    const resultMap = salesToChart.reduce((map, sale) => {
      const day = sale._id?.day;
      if (!day || (!sale.closedSale && !sale.lostSale)) return map;

      const formattedDay = day.split('-').slice(0, 2).join('/');
      const dayTimestamp = new Date(day.split('-').reverse().join('-')).getTime();

      if (!map.has(day)) {
        map.set(day, {
          name: formattedDay,
          timestamp: dayTimestamp,
          series: []
        });
      }

      const group = map.get(day);
      const totalValue = Number(sale.total);

      let statusName = sale.name;
      let color = '';

      if (sale.closedSale === true) {
        color = '#10B981';
      } else if (sale.lostSale === true) {
        color = '#EF4444';
      } else {
        color = this.getDynamicColor(statusName);
      }

      const existingSeries = group.series.find(s => s.name === statusName);
      if (existingSeries) {
        existingSeries.value += totalValue;
      } else {
        group.series.push({
          name: this.t('report.salesMonitor.seriesAttendancesLabel', { name: sale.name, count: sale.attendancesWithStatus }),
          value: totalValue,
          extra: {
            color,
          }
        });
      }
      return map;
    }, new Map<string, ChartGroupItem>());

    this.salesMonitorToBarChart = Array.from(resultMap.values())
      .filter(item => item.series.length > 0)
      .sort((a, b) => a.timestamp! - b.timestamp!)
      .map(({ timestamp, ...rest }) => rest);

    this.customColors = [];
    const uniqueStatuses = new Set<string>();

    this.salesMonitorToBarChart.forEach(item => {
      item.series.forEach(series => {
        if (!uniqueStatuses.has(series.name)) {
          uniqueStatuses.add(series.name);
          this.customColors.push({
            name: series.name,
            value: series.extra?.color || '#000000'
          });
        }
      });
    });
  }

  getSalesBySourcePieChartData(salesBySource: { closedSales: number; source: string }[]): void {
    if (!Array.isArray(salesBySource)) {
      this.salesBySourceChart = [];
      return;
    }
    this.dynamicColors.clear();

    this.salesBySourceChart = salesBySource
      .filter(source => source.closedSales && source.closedSales > 0)
      .map(source => {
        return {
          name: source.source,
          value: source.closedSales,
        };
      });
  }

  getLeadSource(item: any) {
    if (!!item.leadSource) {
      const leadSource = item.leadSource.find((ls: { name?: string }) => ls.name !== undefined);

      if (leadSource !== undefined && leadSource.name !== undefined) {
        this.leadSource = leadSource;
        return leadSource.name;
      }
    }
  }

  private getDynamicColor(statusName: string): string {
    if (this.dynamicColors.has(statusName)) {
      return this.dynamicColors.get(statusName)!;
    }

    const color = this.dynamicColorPalette[this.colorIndex % this.dynamicColorPalette.length];
    this.dynamicColors.set(statusName, color);
    this.colorIndex++;
    return color;
  }
  exportAttendanceReportToExcel(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.salesMonitor.confirmExportExcel'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;

        this.fetchAttendancesRecursively([], new Pager<Attendance>({ perPage: 500 }))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (attendances: Array<Attendance>) => {
              const formattedData = this.formatAttendancesSalesMonitorData(attendances);
              const summaryData: ExcelReportSummaryData = this.getSummaryData(attendances);

              const columns = this.buildColumns([
                [this.t('report.salesMonitor.tableClient'), 'contact'],
                [this.t('report.salesMonitor.tablePhone'), 'phone'],
                [this.t('report.salesMonitor.tableChannel'), 'channel'],
                [this.t('report.salesMonitor.tableDepartment'), 'department'],
                [this.t('report.salesMonitor.tableAttendant'), 'user'],
                [this.t('report.salesMonitor.tableStatus'), 'status'],
                [this.t('report.salesMonitor.tableSource'), 'source'],
                [this.t('report.salesMonitor.tableSaleStatus'), 'salesMonitor'],
                [this.t('report.salesMonitor.exportSaleValue'), 'budget'],
                [this.t('report.salesMonitor.tableClosedAt'), 'closedAt'],
              ]);

              this.exportService.generateExcelReport(formattedData, columns, this.getReportFilters(), this.t('report.salesMonitor.exportReportTitle'), summaryData)
                .then(buffer => this.downloadExcelFile(buffer))
                .finally(() => {
                  this.alertService.success(this.t('report.salesMonitor.excelGeneratedSuccess'));
                  this.isLoading = false;
                });
            },
          });
      }
    });
  }

  async exportAttendanceReportToPDF(): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
      data: this.t('report.salesMonitor.confirmExportPdf'),
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;

        this.fetchAttendancesRecursively([], new Pager<Attendance>({ perPage: 500 }))
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: async (attendances: Array<Attendance>) => {
              const formattedData = this.formatAttendancesSalesMonitorData(attendances);
              const chartsCanvas = await this.getChartsToExport(this.chartContainer);
              const additionalData: any = this.getSummaryData(attendances);
              const columns = this.buildColumns([
                [this.t('report.salesMonitor.tableClient'), 'contact', 70],
                [this.t('report.salesMonitor.tablePhone'), 'phone', 70],
                [this.t('report.salesMonitor.tableChannel'), 'channel', 75],
                [this.t('report.salesMonitor.tableDepartment'), 'department', 75],
                [this.t('report.salesMonitor.tableAttendant'), 'user', 75],
                [this.t('report.salesMonitor.tableStatus'), 'status', 75],
                [this.t('report.salesMonitor.tableSource'), 'source', 75],
                [this.t('report.salesMonitor.tableSaleStatus'), 'salesMonitor', 70],
                [this.t('report.salesMonitor.exportSaleValue'), 'budget', 70],
                [this.t('report.salesMonitor.tableClosedAt'), 'closedAt', 75],
              ]);

              this.exportService.generatePDFReport(formattedData, columns, this.getReportFilters(), this.t('report.salesMonitor.exportReportTitle'), additionalData)
                .finally(() => {
                  this.alertService.success(this.t('report.salesMonitor.pdfGeneratedSuccess'));
                  this.isLoading = false;
                });
            },
          });
      }
    });
  }

  formatAttendancesSalesMonitorData(attendances: Attendance[]) {
    return attendances.map((attendance: Attendance) => {
      const salesMonitorStatus = attendance.salesMonitorStatus;
      return {
        contact: attendance.contact?.name || '',
        phone: attendance.contact?.phone || '',
        channel: attendance.channel?.name || '',
        department: attendance.department?.name || '',
        user: attendance.user?.name || '',
        status: super.getTranslatedStatusAttendance(attendance.status),
        source: this.getLeadSource(attendance.contact),
        salesMonitor: salesMonitorStatus?.salesMonitor?.name || '',
        budget: salesMonitorStatus?.budget ? salesMonitorStatus.budget.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '0,00',
        closedAt: salesMonitorStatus?.closedAt ? DateUtil.toFormart(salesMonitorStatus.closedAt, 'dd/MM/yyyy') : '',
      };
    });
  }

  private getSummaryData(attendances: Attendance[]): ExcelReportSummaryData {
    const kpis = this.getSummaryDataKpis(attendances);
    const { salesByStatus, salesBySource } = this.getChartDataSummary(attendances);
    return { kpis, salesByStatus, salesBySource };
  }

  private getSummaryDataKpis(attendances: Attendance[]): Record<string, string> {
    const salesMonitorStatus = attendances.map(attendance => attendance.salesMonitorStatus);

    const totalSales = salesMonitorStatus.length;

    let closedSale = 0;
    let saleLost = 0;
    let totalValue = 0;

    for (const sm of salesMonitorStatus) {
      const saleClosed = !!sm.salesMonitor?.saleClosed;
      const salesLost = !!sm.salesMonitor?.saleLost;
      const budget = sm.budget ?? 0;
      if (saleClosed) {
        closedSale++;
        totalValue += budget;
      }

      if (salesLost) {
        saleLost++;
      }
    }

    const kpis: Record<string, string> = {
      [this.t('report.salesMonitor.exportKpiTotalAttendances')]: totalSales.toString(),
      [this.t('report.salesMonitor.exportKpiTotalGenerated')]: totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      [this.t('report.salesMonitor.exportKpiClosedSales')]: closedSale.toString(),
      [this.t('report.salesMonitor.exportKpiConversionRate')]: totalSales > 0 ? `${((closedSale / totalSales) * 100).toFixed(2)}%` : '0%',
    };

    return kpis;
  }


  private getChartDataSummary(attendances: Attendance[]): { salesByStatus: ChartGroupItem[]; salesBySource: ChartSeriesItem[]; } {
    const salesMonitorStatus = attendances.map(att => ({ status: att.salesMonitorStatus, contact: att.contact }));

    const statusMap = new Map<string, Map<string, number>>();
    const originMap = new Map<string, number>();

    for (const item of salesMonitorStatus) {
      const att = item.status;
      const statusName = att.salesMonitor?.name ?? this.t('report.salesMonitor.exportNotInformed');
      const budget = att.budget ?? 0;

      const dateKey = att.closedAt
        ? DateUtil.toFormart(att.closedAt, 'dd/MM')
        : this.t('report.salesMonitor.exportNoDate');

      if (!!att.salesMonitor?.saleClosed || !!att.salesMonitor?.saleLost) {
        if (!statusMap.has(dateKey)) statusMap.set(dateKey, new Map());
        const statusGroup = statusMap.get(dateKey)!;
        statusGroup.set(statusName, (statusGroup.get(statusName) ?? 0) + budget);
      }

      if (att.salesMonitor?.saleClosed) {
        const origem = this.getLeadSource(item.contact) ?? this.t('report.salesMonitor.exportNotInformed');
        originMap.set(origem, (originMap.get(origem) ?? 0) + 1);
      }
    }

    const salesByStatus: ChartGroupItem[] = Array.from(statusMap.entries()).map(
      ([date, group]) => ({
        name: date,
        series: Array.from(group.entries()).map(([status, value]) => ({
          name: status,
          value,
        })),
      })
    );

    const salesBySource: ChartSeriesItem[] = Array.from(originMap.entries()).map(
      ([name, value]) => ({
        name,
        value,
      })
    );

    return { salesByStatus, salesBySource };
  }


  private getReportFilters(): Record<string, string> {
    const values = this.formGroup.getRawValue();

    const filters: Record<string, string> = {};

    if (values.channel) {
      const channel = this.listChannel.find(c => c._id === values.channel);
      if (channel) filters[this.t('report.salesMonitor.tableChannel')] = channel.name;
    }

    if (values.department) {
      const dept = this.listDepartment.find(d => d._id === values.department);
      if (dept) filters[this.t('report.salesMonitor.tableDepartment')] = dept.name;
    }

    if (values.user) {
      const user = this.listUser.find(u => u._id === values.user);
      if (user) filters[this.t('report.salesMonitor.tableAttendant')] = user.name;
    }

    if (values.salesMonitor) {
      const monitor = this.listSalesMonitorChanel.find(m => m._id === values.salesMonitor);
      if (monitor) filters[this.t('report.salesMonitor.tableSaleStatus')] = monitor.name;
    }

    if (values.dateRange?.dateStart && values.dateRange?.dateEnd) {
      filters[this.t('report.salesMonitor.exportFilterPeriod')] = `${DateUtil.toFormart(values.dateRange.dateStart, 'dd/MM/yyyy')} - ${DateUtil.toFormart(values.dateRange.dateEnd, 'dd/MM/yyyy')}`;
    }

    return filters;
  }

  private downloadExcelFile(buffer: any): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Trendmulti_Relatório_Atendimentos_Monitor_de_Vendas${new Date().toLocaleDateString()}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  buildColumns(keys: Array<[string, string, number?]>): any[] {
    return keys.map(([header, key, width = 20]) => ({
      header: header,
      key,
      width,
    }));
  }

  async getChartsToExport(element: ElementRef | undefined): Promise<any[]> {
    if (!element) {
      console.warn('Element reference is undefined in getChartsToExport');
      return [
        { text: this.t('report.salesMonitor.exportSalesReportTitle'), style: 'header', margin: [0, 0, 0, 10] }
      ];
    }
    
    const canvas = await html2canvas(element.nativeElement, { scale: 1.5, useCORS: true, logging: false });
    const imgData = canvas.toDataURL('image/png');

    const a4Width = 595.28;
    const aspectRatio = canvas.height / canvas.width;

    const width = a4Width;
    const height = a4Width * aspectRatio;

    return [
      { text: this.t('report.salesMonitor.exportSalesReportTitle'), style: 'header', margin: [0, 0, 0, 10] },
      {
        image: imgData,
        width: width,
        height: height,
        alignment: 'center',
        margin: [0, 0, 0, 20],
      },
    ];
  }

  translateStatus(status: AttendanceStatusEnum): string {
    switch (status) {
      case AttendanceStatusEnum.IN_PROGRESS:
        return this.t('report.salesMonitor.statusInProgress');
      case AttendanceStatusEnum.PENDING:
        return this.t('report.salesMonitor.statusPending');
      case AttendanceStatusEnum.OVERTIME:
        return this.t('report.salesMonitor.statusOvertime');
      case AttendanceStatusEnum.PAUSED:
        return this.t('report.salesMonitor.statusPaused');
      case AttendanceStatusEnum.CLOSED:
        return this.t('report.salesMonitor.statusClosed');
      default:
        return this.t('report.salesMonitor.statusUnknown');
    }
  }

  notify(item: any): void {
    if (!!item) {
      this.router.navigate(['/attendance/panel'], { queryParams: { id: item?._id, status: item?.status } });
    }
  }
}
